import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from "react-redux";
import Grid from '@mui/material/Grid2'
import { Modal, Divider, Button, Fade, Backdrop, Paper, IconButton, TextField } from '@mui/material'
import Alert from '@mui/material/Alert'
import CloseIcon from '@mui/icons-material/Close'

import { UserApi } from '../../api/userApi'
import {setSuccessNotification} from "../../actions/notificationAction";
import useTranslation from '../../hooks/useTranslation'

const styles = {
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    width: { xs: '95%', sm: '70%', md: '60%', lg: '50%' },
    boxShadow: 24,
    borderRadius: '12px',
    overflow: 'hidden'
  },
  tableContainer: {
    overflow: 'scroll',
    maxHeight: 300,
    padding: '2%', 
    borderRadius: '5px'
  },
  container: {
    padding: '2%'
  },
  header: {
    padding: '16px 20px',
    backgroundColor: '#fafafa'
  },
  headerTitle: {
    margin: 0
  },
  headerSubtitle: {
    margin: '4px 0 0 0',
    fontSize: '0.9rem',
    color: '#666'
  },
  body: {
    padding: '16px 20px 6px 20px'
  },
  footer: {
    padding: '12px 20px',
    backgroundColor: '#fafafa'
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px'
  }
}

const Invite = props => {
  const initialState = {email: '', balance: 0, discount: '', notification_email: ''}
  const [invite, setInvite] = useState(initialState)
  const [inviting, setInviting] = useState(false)
  const [error, setError] = useState({})
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const sendInvite = e => {
    e.preventDefault()

    if(invite.email.length) {
      setInviting(true)
      UserApi.sendInvite(invite)
        .then(res => {
          if(res.data.status === 200) {
            setError(
              {
                name: 'User Exists',
                message: res.data.message,
                type: 'warning'
              }
            )
          }
          else {
            if(props.usersByTab.invited && props.tabCounts.invited < props.perPage) {
              let _usersByTab = {...props.usersByTab}
              _usersByTab.invited.push(res.data.user)
              props.setUsersByTab(_usersByTab)
            }
            props.setTabCounts({ ...props.tabCounts, invited: props.tabCounts.invited + 1 })
            setInvite(initialState)
            dispatch(setSuccessNotification({ message: t('response_messages.invitation_sent_successfully') }))
          }
          setInviting(false)
        })
        .catch(err => {
          setError({
            name: 'Internal server error',
            message: 'Something went wrong while sending invite to user. Please try again later!',
            type: 'danger'
          })
          setInviting(false)
        })
    }
  }

  const clearErrors = () => {
    setError({})
  }

  return (
    <Modal
      open={props.open}
      onClose={props.onCloseItemModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }} >
      <Fade in={props.open}>
        <Paper sx={styles.modal}>
          <Grid container alignItems="center">
            <Grid container size={12} alignItems="center" sx={styles.header}>
              <Grid size={{xs:10}}>
                <h3 style={styles.headerTitle}>{t('labels.invite_users_title')}</h3>
                <p style={styles.headerSubtitle}>{t('labels.invite_users_subtitle')}</p>
              </Grid>
              <Grid size={{xs:2}} textAlign="right">
                <IconButton onClick={props.onCloseItemModal}><CloseIcon /></IconButton>
              </Grid>
            </Grid>
            <Divider width='100%' sx={{width: '100%'}} />
            {error.message && error.message.length && (
              <Grid container item xs={12} sx={{padding: '12px 20px 0 20px'}}>
                <Alert severity="warning" onClose={clearErrors} sx={{width: '100%'}}>
                  {error.message}
                </Alert>
              </Grid>
            )}
            <Grid container sx={styles.body} spacing={2}>
              <Grid item size={{xs:12,sm:4}}>
                <TextField
                  required
                  type="email"
                  label={t('labels.email_label')}
                  name="email"
                  value={invite.email}
                  placeholder={t('labels.email_placeholder')}
                  onChange={e =>setInvite({...invite, email: e.target.value})}
                  helperText={t('labels.email_helper')}
                  fullWidth
                />
              </Grid>
              <Grid item size={{xs:12,sm:4}}>
                <TextField
                  type="email"
                  label={t('labels.notification_email_label')}
                  name="notification_email"
                  value={invite.notification_email}
                  placeholder={t('labels.notification_email_placeholder')}
                  onChange={e => setInvite({ ...invite, notification_email: e.target.value })}
                  helperText={t('labels.notification_email_helper')}
                  fullWidth
                />
              </Grid>
              <Grid item size={{xs:12,sm:4}}>
                <TextField
                  required
                  label={t('labels.balance_label')}
                  type="number"
                  name="balance"
                  value={invite.balance}
                  onChange={e =>setInvite({...invite, balance: e.target.value})}
                  helperText={t('labels.balance_helper')}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Grid>
              <Grid item size={{xs:12,sm:4}}>
                <TextField
                  label={t('labels.discount_label')}
                  name="discount"
                  value={`${invite.discount}`}
                  onChange={e =>setInvite({...invite, discount: e.target.value})}
                  helperText={t('labels.discount_helper')}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Divider width="100%" sx={{width: '100%'}} />
            <Grid container item size={{xs:12}} alignItems="center" sx={styles.footer}>
              <Grid item size={{xs:12}} sx={styles.footerActions}>
                <Button variant='outlined' onClick={props.onCloseItemModal}>{t('labels.close_button')}</Button>
                <Button variant="contained" color="primary" disabled={inviting} onClick={sendInvite}>
                  {inviting ? t('labels.sending_button') : t('labels.send_invite_button')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Fade>
    </Modal>
  )
}
Invite.propTypes = {  
  open: PropTypes.bool.isRequired,
  onCloseItemModal: PropTypes.func.isRequired,
  usersByTab: PropTypes.object.isRequired,
  tabCounts: PropTypes.object.isRequired,
  setUsersByTab: PropTypes.func.isRequired,
  setTabCounts: PropTypes.func.isRequired,
  perPage: PropTypes.number.isRequired
}

export default Invite